import { useLazyQuery, useMutation, useQuery, useSubscription } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { useEffect } from 'react'

import type {
  DashboardProjectsQueryVariables,
  ProjectAndGroupTrashCountQuery,
  ProjectAndGroupTrashCountQueryVariables,
  ProjectAndGroupTrashCountSubscriptionSubscriptionVariables,
  ProjectAndGroupTrashQueryVariables,
  ProjectGroupsAllQueryVariables,
  ProjectGroupsGetByIdQueryVariables,
} from 'gql/__generated__/graphql'

import {
  catalogDuplicate,
  catalogForceDelete,
  catalogMove,
  projectGroupsCreate,
  projectGroupsMultiRestoreByIds,
  projectGroupsUpdate,
} from './gql/mutations'
import {
  dashboardProjects,
  projectAndGroupTrash,
  projectAndGroupTrashCount,
  projectGroupAll,
  projectGroupsByIdQuery,
} from './gql/queries'
import { projectAndGroupTrashCountSubscription } from './gql/subscriptions'

export const useDashboardProjects = (variables: DashboardProjectsQueryVariables) =>
  useQuery(dashboardProjects, {
    variables,
    fetchPolicy: 'cache-and-network',
  })

export const useProjectGroupsGetById = (variables: ProjectGroupsGetByIdQueryVariables) =>
  useQuery(projectGroupsByIdQuery, {
    variables,
  })

export const useProjectGroupAll = (variables: ProjectGroupsAllQueryVariables) =>
  useQuery(projectGroupAll, {
    fetchPolicy: 'cache-and-network',
    variables,
    onError: (err) =>
      console.error('"useProjectGroupAll" fn is crashed on operation: "useQuery"', err),
  })

export const useProjectGroupAllLazy = (variables: ProjectGroupsAllQueryVariables) =>
  useLazyQuery(projectGroupAll, {
    fetchPolicy: 'cache-and-network',
    variables,
    onError: (err) =>
      console.error('"useProjectGroupAllLazy" fn is crashed on operation: "useLazyQuery"', err),
  })

export const useProjectAndGroupTrash = (variables: ProjectAndGroupTrashQueryVariables) =>
  useQuery(projectAndGroupTrash, {
    variables,
    fetchPolicy: 'cache-and-network',
    onError: (err) =>
      console.error('"useProjectAndGroupTrash" fn is crashed on operation: "useQuery"', err),
  })

export const useProjectAndGroupTrashCountSunscription = (
  variables: ProjectAndGroupTrashCountSubscriptionSubscriptionVariables,
) => useSubscription(projectAndGroupTrashCountSubscription, { variables })

export const useProjectAndGroupTrashCount = (
  variables: ProjectAndGroupTrashCountQueryVariables,
) => {
  const { data, subscribeToMore } = useQuery(projectAndGroupTrashCount, {
    variables,
    fetchPolicy: 'cache-and-network',
  })
  useEffect(() => {
    subscribeToMore({
      document: projectAndGroupTrashCountSubscription,
      variables,
      updateQuery: (prev, { subscriptionData }): ProjectAndGroupTrashCountQuery => {
        if (!subscriptionData.data) {
          return prev
        }

        return {
          ...prev,
          data: {
            count: subscriptionData.data.data.count,
          },
        }
      },
    })
  }, [subscribeToMore, variables])
  return { data }
}

export const useProjectGroupsRestored = () =>
  useMutation(projectGroupsMultiRestoreByIds, {
    refetchQueries: [
      String(getOperationName(projectAndGroupTrash)),
      String(getOperationName(projectGroupAll)),
    ],
  })

export const useProjectGroupsCreate = () =>
  useMutation(projectGroupsCreate, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
    onError: (err) =>
      console.error('"useProjectGroupsCreate" fn is crashed on operation: "useMutation"', err),
  })

export const useProjectGroupsUpdate = () =>
  useMutation(projectGroupsUpdate, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
    onError: (err) =>
      console.error('"useProjectGroupsUpdate" fn is crashed on operation: "useMutation"', err),
  })

export const useCatalogForceDelete = () =>
  useMutation(catalogForceDelete, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
    onError: (err) =>
      console.error('"useCatalogForceDelete" fn is crashed on operation: "useMutation"', err),
  })

export const useCatalogMove = () =>
  useMutation(catalogMove, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
    onError: (err) =>
      console.error('"useCatalogMove" fn is crashed on operation: "useMutation"', err),
  })

export const useCatalogDuplicate = () =>
  useMutation(catalogDuplicate, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
  })
